import React, { useEffect, useState, useRef } from 'react';
import { api, AED } from '../lib/api';
import { X, Upload, Loader2, Sparkles, FileText, CheckCircle2, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

export default function BOQUploadModal({ open, onClose, propertyId, onCreated }) {
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [title, setTitle] = useState('');
  const [runAiExtract, setRunAiExtract] = useState(false);
  const [runAudit, setRunAudit] = useState(false);
  const [negotiatedTotal, setNegotiatedTotal] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFile(null); setResult(null); setTitle(''); setNegotiatedTotal('');
    setRunAiExtract(false); setRunAudit(false);
    api.get('/vendors').then(r => {
      setVendors(r.data || []);
      if (r.data && r.data[0] && !vendorId) setVendorId(r.data[0].id);
    }).catch(() => {});
  }, [open]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ok = /\.(pdf|xlsx|xls|csv)$/i.test(f.name);
    if (!ok) { toast.error('Only PDF, Excel or CSV are supported.'); return; }
    if (f.size > 25 * 1024 * 1024) { toast.error('Max size 25 MB.'); return; }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const submit = async () => {
    if (!file) { toast.error('Pick a BOQ file first.'); return; }
    if (!vendorId) { toast.error('Pick a vendor.'); return; }
    setBusy(true); setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('property_id', propertyId);
      form.append('vendor_id', vendorId);
      if (title) form.append('title', title);
      form.append('run_ai_extract', runAiExtract ? 'true' : 'false');
      form.append('run_audit', (runAiExtract && runAudit) ? 'true' : 'false');
      if (negotiatedTotal && parseFloat(negotiatedTotal) > 0) {
        form.append('negotiated_total_aed', parseFloat(negotiatedTotal));
      }
      const r = await api.post('/boqs/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000, // AI normalization can take 30–90s
      });
      setResult(r.data);
      toast.success(`Ingested ${r.data.lines_count} line items`);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'BOQ upload failed');
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-md grid place-items-center p-4"
          onClick={onClose}
          data-testid="boq-upload-modal"
        >
          <motion.div
            initial={{ scale: 0.96, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
          >
            <div className="p-5 border-b border-slate-100 flex items-start gap-4 shrink-0">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#679286] to-[#4a6d63] grid place-items-center shadow-lg">
                <Upload size={22} className="text-white"/>
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                  <Sparkles size={10}/> Iris AI · BOQ import
                </div>
                <h2 className="font-serif text-2xl">Upload BOQ</h2>
                <div className="text-xs text-slate-500">PDF, Excel (.xlsx/.xls) or CSV. Iris will extract line items and audit for fraud.</div>
              </div>
              <button onClick={onClose} data-testid="boq-upload-close"
                className="w-9 h-9 rounded-full hover:bg-slate-100 grid place-items-center"><X size={18}/></button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              {!result ? (
                <>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Vendor</div>
                    <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                      data-testid="boq-upload-vendor"
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm">
                      <option value="">— Select vendor —</option>
                      {vendors.map(v => (
                        <option key={v.id} value={v.id}>
                          {v.company || v.name}{v.company && v.name ? ` — ${v.name}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Title (optional)</div>
                    <input value={title} onChange={(e) => setTitle(e.target.value)}
                      data-testid="boq-upload-title"
                      placeholder="e.g. Marina Apt 1502 — Full refurb quote"
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"/>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">File</div>
                    <div
                      onClick={() => inputRef.current?.click()}
                      data-testid="boq-upload-dropzone"
                      className="rounded-2xl border-2 border-dashed border-slate-300 hover:border-[#679286] transition p-8 text-center cursor-pointer bg-slate-50/40">
                      {file ? (
                        <div className="flex items-center justify-center gap-2 text-slate-700 flex-wrap">
                          <FileText size={18} className="text-[#679286]"/>
                          <span className="text-sm font-medium">{file.name}</span>
                          <span className="text-xs text-slate-400">· {(file.size / 1024).toFixed(0)} KB</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const url = URL.createObjectURL(file);
                              window.open(url, '_blank', 'noopener');
                              // Revoke after a beat so the tab has time to load.
                              setTimeout(() => URL.revokeObjectURL(url), 10000);
                            }}
                            data-testid="boq-upload-preview"
                            className="text-xs text-[#679286] hover:underline inline-flex items-center gap-1">
                            Preview
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setFile(null); }}
                            data-testid="boq-upload-clear"
                            className="text-xs text-slate-500 hover:text-red-600 hover:underline">
                            Change
                          </button>
                        </div>
                      ) : (
                        <div className="text-slate-500">
                          <Upload size={22} className="mx-auto mb-2 text-slate-400"/>
                          <div className="text-sm">Click to select a BOQ file</div>
                          <div className="text-xs text-slate-400 mt-1">PDF · Excel · CSV up to 25 MB</div>
                        </div>
                      )}
                      <input ref={inputRef} type="file" accept=".pdf,.xlsx,.xls,.csv"
                        onChange={onPickFile} className="hidden"
                        data-testid="boq-upload-file"/>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={runAiExtract} onChange={(e) => setRunAiExtract(e.target.checked)}
                      data-testid="boq-upload-run-ai"
                      className="w-4 h-4 accent-[#679286]"/>
                    <span>
                      Extract line items with Iris AI now
                      <span className="block text-[11px] text-slate-500">Uses LLM credits. Leave unchecked to just save the file + total; you can analyse later.</span>
                    </span>
                  </label>
                  {runAiExtract && (
                    <label className="flex items-center gap-2 text-sm text-slate-700 ml-6">
                      <input type="checkbox" checked={runAudit} onChange={(e) => setRunAudit(e.target.checked)}
                        data-testid="boq-upload-audit"
                        className="w-4 h-4 accent-[#679286]"/>
                      Also run fraud audit
                    </label>
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
                      Negotiated final total (optional)
                      <span className="normal-case tracking-normal text-slate-400 text-[11px]">— overrides the file&apos;s line-sum</span>
                    </div>
                    <input type="number" min="0" step="100"
                      value={negotiatedTotal}
                      onChange={(e) => setNegotiatedTotal(e.target.value)}
                      placeholder="e.g. 285000 (leave blank to trust file sum)"
                      data-testid="boq-upload-negotiated-total"
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm font-mono"/>
                    <div className="text-[11px] text-slate-500 mt-1">
                      Excel BOQs often mis-parse decimals or exclude a discount. If you agreed a lump-sum with the vendor, put it here — Iris will keep the raw line items but use this number for budget vs actuals.
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-2xl p-4 bg-emerald-50 border border-emerald-200 flex items-start gap-3">
                    <CheckCircle2 size={22} className="text-emerald-600 mt-0.5"/>
                    <div>
                      <div className="font-serif text-lg">BOQ saved</div>
                      <div className="text-sm text-slate-700 mt-0.5">
                        {result.lines_count > 0
                          ? <>{result.lines_count} line items · Total <span className="font-semibold">{AED(result.boq?.total_aed)}</span></>
                          : <>File attached · Total <span className="font-semibold">{AED(result.boq?.total_aed)}</span> (no line items yet — click <b>Analyze with Iris AI</b> on the BOQ card to extract)</>
                        }
                      </div>
                      {result.ai_error && (
                        <div className="text-xs text-amber-700 mt-1">AI note: {result.ai_error}</div>
                      )}
                      {result.vendor_name_guess && (
                        <div className="text-xs text-slate-500 mt-1">Iris detected vendor: <span className="font-medium">{result.vendor_name_guess}</span></div>
                      )}
                    </div>
                  </div>
                  {result.audit && (
                    <div className={`rounded-2xl p-4 border ${
                      (result.audit.fraud_score || 0) >= 60 ? 'bg-red-50 border-red-200'
                      : (result.audit.fraud_score || 0) >= 30 ? 'bg-amber-50 border-amber-200'
                      : 'bg-slate-50 border-slate-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle size={16} className={
                          (result.audit.fraud_score || 0) >= 60 ? 'text-red-600'
                          : (result.audit.fraud_score || 0) >= 30 ? 'text-amber-600'
                          : 'text-slate-500'
                        }/>
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-700">
                          Iris audit · Risk score {Math.round(result.audit.fraud_score || 0)}/100
                        </div>
                      </div>
                      <div className="text-sm text-slate-700">{result.audit.summary}</div>
                      {(result.audit.flags || []).length > 0 && (
                        <ul className="mt-2 text-xs text-slate-700 space-y-1 list-disc pl-4">
                          {result.audit.flags.slice(0, 5).map((f, i) => <li key={i}><span className="font-medium">Line {f.line_number}:</span> {f.note}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50/60 shrink-0">
              {!result ? (
                <>
                  <button onClick={onClose} className="px-4 py-2 rounded-full border border-slate-200 text-sm hover:bg-white">Cancel</button>
                  <button onClick={submit} disabled={busy || !file || !vendorId}
                    data-testid="boq-upload-submit"
                    className="px-5 py-2 rounded-full bg-[#679286] text-white text-sm font-semibold hover:bg-[#4a6d63] transition disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2">
                    {busy ? <><Loader2 size={14} className="animate-spin"/> Saving BOQ…</> : <><Upload size={14}/> Save BOQ</>}
                  </button>
                </>
              ) : (
                <button onClick={onClose} data-testid="boq-upload-done"
                  className="px-5 py-2 rounded-full bg-[#679286] text-white text-sm font-semibold">
                  Done
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
